import { supabase } from '../lib/supabase';

const isSchemaError = (error) => {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') return true;
    if (errorClass === '23') return false;
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
};

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const technicianService = {
  async getAll() {
    try {
      const { data, error } = await supabase
        ?.from('technician_profiles')
        ?.select(`
          *,
          user:user_profiles(*)
        `)
        ?.order('current_workload', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching technician profiles:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch technician profiles:', error);
      throw error;
    }
  },

  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('technician_profiles')
        ?.select(`
          *,
          user:user_profiles(*)
        `)
        ?.eq('id', id)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching technician profile:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to fetch technician profile:', error);
      throw error;
    }
  },

  async getAvailable(vehicleType) {
    try {
      let specialization = 'both';
      if (vehicleType === 'motocicleta') {
        specialization = 'moto';
      } else if (['auto', 'camioneta', 'suv', 'van']?.includes(vehicleType)) {
        specialization = 'auto';
      }

      const { data, error } = await supabase
        ?.from('technician_profiles')
        ?.select(`
          *,
          user:user_profiles!inner(*)
        `)
        ?.eq('availability_status', 'available')
        ?.eq('user.is_active', true)
        ?.or(`vehicle_specialization.eq.${specialization},vehicle_specialization.eq.both`)
        ?.order('current_workload', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching available technicians:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch available technicians:', error);
      throw error;
    }
  },

  async updateAvailability(id, availabilityStatus) {
    try {
      const { data, error } = await supabase
        ?.from('technician_profiles')
        ?.update({ availability_status: availabilityStatus })
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update technician availability:', error);
      throw error;
    }
  },

  async update(id, technicianData) {
    try {
      const snakeData = toSnakeCase(technicianData);

      const { data, error } = await supabase
        ?.from('technician_profiles')
        ?.update(snakeData)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to update technician profile:', error);
      throw error;
    }
  },

  async create(technicianData) {
    try {
      const snakeData = toSnakeCase(technicianData);

      const { data, error } = await supabase
        ?.from('technician_profiles')
        ?.insert(snakeData)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Failed to create technician profile:', error);
      throw error;
    }
  },

  async getWorkload(technicianId) {
    try {
      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select('id, folio_ot, estado_ot')
        ?.eq('assigned_to', technicianId)
        ?.in('estado_ot', ['recepcionado', 'inspeccionado', 'cotizando', 'aprobado', 'enproceso']);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching technician workload:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch technician workload:', error);
      throw error;
    }
  }
};